import { t } from '@trimble/i18n';
import {
  AlignmentMeasurementCapability,
  AngleMeasurementCapability,
  LineMeasurementCapability,
  PointMeasurementCapability,
  PolylineMeasurementCapability,
  SimpleMeasurementCapabilityBase,
  SnappingCapability,
} from '@trimble/web3d-capabilities';
import {
  MeasurementsCapabilityProvider,
  isLineMeasurement,
  isAlignmentMeasurement,
  isPointMeasurement,
  isAngleMeasurement,
  isPolylineMeasurement,
  LineMeasurementType,
  AlignmentMeasurementType,
  PointMeasurementType,
  AngleMeasurementType,
  PolylineMeasurementType,
  pointPositionFormatter,
} from '@trimble/web3d-providers';
import {
  Component,
  createMemo,
  createSignal,
  For,
  Show,
  Switch,
  Match,
} from 'solid-js';
import { MeasurementAlignmentItem } from './item/MeasurementAlignmentItem';
import { MeasurementAngleItem } from './item/MeasurementAngleItem';
import { MeasurementLineItem } from './item/MeasurementLineItem';
import { MeasurementPointItem } from './item/MeasurementPointItem';
import { MeasurementPolylineItem } from './item/MeasurementPolylineItem';
import { MeasurementToolbar } from './MeasurementToolbar';
import { MeasurementControls } from './MeasurementControls';
import { PolylineMeasurementOptions } from '@trimble/measurements-details';
import { ColorPicker } from '@trimble/ui-components/solidjs';
import {
  rgbColorToCssHexString,
  calculateHorizontalDistance,
  calculateVerticalDistance,
} from '@trimble/utilities';
import { AlignmentSettings } from '@trimble/types';
import { RgbColor } from 'solid-color';
import { Portal } from 'solid-js/web';
import { EditPointMeasurement } from './item/actions/EditPointMeasurement';
import { Color } from '@technology/web3d';
import { PointMarkup } from '@technology/web3d-plugin-markup';
import tinycolor from 'tinycolor2';
import { BtnClasses, Button, PanelLayout } from '@tcweb/connect-ui';
import { PointMeasurementOptions } from '@trimble/measurements-details';

export interface MeasurementsTabProps {
  measurementsProvider: MeasurementsCapabilityProvider;
  pointMeasurementCapability?: PointMeasurementCapability;
  lineMeasurementCapability?: LineMeasurementCapability;
  angleMeasurementCapability?: AngleMeasurementCapability;
  alignmentMeasurementCapability?: AlignmentMeasurementCapability;
  snappingCapability?: SnappingCapability;
  polylineMeasurementCapability?: PolylineMeasurementCapability;
  allowCustomMeasurementColor?: boolean;
  showSaveButton?: boolean;
  hideMeasurementItems?: boolean;
  hideDeleteAllButton?: boolean;
  onMeasurementClick(measurementId: string): void;
  alignmentSettings?: AlignmentSettings;
  onClose?(): void;
  onSaveMeasurements?(): void;
}

const defaultColor: RgbColor = { r: 236, g: 0, b: 182 }; // Default color #EC00B6

export const MeasurementsTab: Component<MeasurementsTabProps> = (props) => {
  const [showColorPicker, setShowColorPicker] = createSignal<boolean>(false);
  const [selectedColor, setSelectedColor] =
    createSignal<RgbColor>(defaultColor);
  const [editingMeasurement, setEditingMeasurement] =
    createSignal<PointMeasurementType | null>(null);
  const [showConfig, setShowConfig] = createSignal(true);

  const handleColorSave = (color: RgbColor): void => {
    setSelectedColor(color);
    setShowColorPicker(false);
    props.measurementsProvider.updateMarkupColor(rgbColorToCssHexString(color));
  };

  const getRGBColorString = (color: RgbColor): string => {
    const rColor = String(color.r);
    const gColor = String(color.g);
    const bColor = String(color.b);
    return `rgb(${rColor},${gColor},${bColor})`;
  };

  const convertColorToRgbColor = (color: Color): RgbColor => {
    const rgb = color.getRGB({ r: 0, g: 0, b: 0 });
    return {
      // eslint-disable-next-line no-magic-numbers
      r: Math.round(rgb.r * 255),
      // eslint-disable-next-line no-magic-numbers
      g: Math.round(rgb.g * 255),
      // eslint-disable-next-line no-magic-numbers
      b: Math.round(rgb.b * 255),
    };
  };

  const handleDelete = (measurementId: string): void => {
    props.measurementsProvider.removeMeasurement(measurementId);
  };

  let editPointModalRef: HTMLModusModalElement | undefined;

  const setEditPointModalRef = (el: HTMLModusModalElement): void => {
    editPointModalRef = el;
  };

  const handleEditPointMeasurement = (): void => {
    void editPointModalRef?.open();
  };

  const handleEditPointModalClose = (): void => {
    void editPointModalRef?.close();
  };

  const editingColor = createMemo(() => {
    return editingMeasurement()
      ? convertColorToRgbColor(editingMeasurement()!.markups[0].color)
      : defaultColor;
  });

  const handleEditPointModalSave = (label: string, color: RgbColor): void => {
    setEditingMeasurement((prev) => {
      if (!prev) return prev;
      const markups = prev.markups;
      markups[0].color = new Color(rgbColorToCssHexString(color));
      const viewerWrapper = props.measurementsProvider.viewerWrappers()[0];
      (markups[0] as PointMarkup).positionFormatter = (position) =>
        pointPositionFormatter(
          position,
          viewerWrapper.viewer,
          prev.showXYCoordinates,
          prev.showZCoordinate,
          label
        );
      const updated: PointMeasurementType = {
        ...prev,
        markups: markups,
        color: tinycolor(color).toRgb(),
        label: label,
      };
      props.measurementsProvider.updateMeasurement(updated);
      return null;
    });
  };

  const handleMeasurementToggle = (
    measurementCapability:
      | SimpleMeasurementCapabilityBase
      | AlignmentMeasurementCapability
  ): void => {
    if (!measurementCapability.isActive()) {
      void measurementCapability.startMeasurement();
      setShowConfig(true);
    } else {
      void measurementCapability.stopMeasurement();
      setShowConfig(false);
    }
  };

  let colorBtnRef: HTMLButtonElement | undefined;
  const [colorPickerPosition, setColorPickerPosition] = createSignal({
    left: 0,
    top: 0,
  });

  const openColorPicker = (): void => {
    if (colorBtnRef) {
      const rect = colorBtnRef.getBoundingClientRect();
      // eslint-disable-next-line no-magic-numbers
      setColorPickerPosition({ left: rect.left, top: rect.bottom + 250 });
    }
    setShowColorPicker(true);
  };

  return (
    <div data-testid="measurements-tab" class="contents">
      <PanelLayout
        panelTitle={t('measurements.measurements')}
        rightActions={
          <Show when={props.onClose}>
            <modus-wc-icon
              data-testid="measurements-tab-close"
              name="close"
              size="lg"
              custom-class="cursor-pointer"
              onClick={() => {
                props.onClose!();
              }}
            />
          </Show>
        }
        content={
          <div class="flex flex-col h-full">
            <div class="p-2 border-b border-gray-300">
              <MeasurementToolbar
                polylineMeasurementCapability={
                  props.polylineMeasurementCapability
                }
                lineMeasurementCapability={props.lineMeasurementCapability}
                pointMeasurementCapability={props.pointMeasurementCapability}
                angleMeasurementCapability={props.angleMeasurementCapability}
                alignmentMeasurementCapability={
                  props.alignmentMeasurementCapability
                }
                supportsVolumetricMeasurements={false}
                supportsGroundworksMeasurements={false}
                measurementsProvider={props.measurementsProvider}
                hideDeleteAllButton={props.hideDeleteAllButton}
                orientation={'horizontal'}
                onMeasurementToggle={handleMeasurementToggle}
                onClearAll={() => {
                  props.measurementsProvider.removeAllMeasurements();
                }}
              />
            </div>
            <Show when={props.allowCustomMeasurementColor}>
              <div class="flex items-center gap-2 py-2 px-2 border-b border-gray-300">
                <div class="mr-2">{t('measurements.selectColor')}</div>
                <button
                  ref={colorBtnRef}
                  class="w-6 h-6 rounded-full border border-gray-300 cursor-pointer"
                  style={{
                    ['background-color']: getRGBColorString(selectedColor()),
                  }}
                  onClick={openColorPicker}
                />
                <Show when={showColorPicker()}>
                  <Portal>
                    <div
                      class="fixed z-dropdown"
                      style={{
                        left: `${String(colorPickerPosition().left)}px`,
                        top: `${String(colorPickerPosition().top)}px`,
                      }}
                    >
                      <ColorPicker
                        initialColor={getRGBColorString(selectedColor())}
                        onSave={handleColorSave}
                      />
                    </div>
                  </Portal>
                </Show>
              </div>
            </Show>
            <div class="flex flex-col gap-2">
              <div class="flex items-center justify-between px-2 pt-2">
                <span class="font-semibold text-gray-8">
                  {t('measurements.configure')}
                </span>
                <i
                  class={`icon-font i32 cursor-pointer ${showConfig() ? 'tc-icon-arrow-drop-up' : 'tc-icon-arrow-drop-down'}`}
                  on:click={() => setShowConfig(!showConfig())}
                />
              </div>
              <Show when={showConfig()}>
                <Show when={props.polylineMeasurementCapability?.isActive()}>
                  <PolylineMeasurementOptions
                    polylineMeasurementCapability={
                      props.polylineMeasurementCapability
                    }
                  />
                </Show>
                <Show when={props.pointMeasurementCapability?.isActive()}>
                  <PointMeasurementOptions
                    measurementsProvider={props.measurementsProvider}
                  />
                </Show>
                <div class="flex border-b border-gray-300 p-2 items-center justify-center">
                  <MeasurementControls
                    snappingCapability={props.snappingCapability}
                    alignmentMeasurementCapability={
                      props.alignmentMeasurementCapability
                    }
                  />
                </div>
              </Show>
            </div>
            <Show when={!props.hideMeasurementItems}>
              <div class="flex flex-col flex-1 overflow-y-auto overflow-x-hidden min-h-0 p-2">
                <For each={props.measurementsProvider.items()}>
                  {(measurement) => (
                    <Switch>
                      <Match when={isLineMeasurement(measurement)}>
                        <MeasurementLineItem
                          value={(measurement as LineMeasurementType).value}
                          formattingSettings={props.measurementsProvider.formattingSettings()}
                          baseLengthUnit={props.measurementsProvider.lengthUnit}
                          onDelete={() => {
                            handleDelete(measurement.id);
                          }}
                          onClick={() => {
                            props.onMeasurementClick?.(measurement.id);
                          }}
                          isSelected={props.measurementsProvider.isItemSelected(
                            measurement.id
                          )}
                          color={measurement.markups[0].color}
                        />
                      </Match>
                      <Match when={isAlignmentMeasurement(measurement)}>
                        {(() => {
                          const alignmentMeasurement =
                            measurement as AlignmentMeasurementType;
                          const horizontal = calculateHorizontalDistance(
                            alignmentMeasurement.alignment,
                            alignmentMeasurement.point
                          );
                          const vertical = calculateVerticalDistance(
                            alignmentMeasurement.alignment,
                            alignmentMeasurement.point
                          );
                          return (
                            <MeasurementAlignmentItem
                              value={alignmentMeasurement.value}
                              horizontal={horizontal}
                              vertical={vertical}
                              baseLengthUnit={
                                props.measurementsProvider.lengthUnit
                              }
                              formattingSettings={props.measurementsProvider.formattingSettings()}
                              alignmentSettings={props.alignmentSettings}
                              onDelete={() => {
                                handleDelete(measurement.id);
                              }}
                              onClick={() => {
                                props.onMeasurementClick?.(measurement.id);
                              }}
                              isSelected={props.measurementsProvider.isItemSelected(
                                measurement.id
                              )}
                              color={alignmentMeasurement.markups[0].color}
                            />
                          );
                        })()}
                      </Match>
                      <Match when={isPointMeasurement(measurement)}>
                        {(() => {
                          const pointMeasurement =
                            measurement as PointMeasurementType;
                          return (
                            <MeasurementPointItem
                              x={pointMeasurement.x}
                              y={pointMeasurement.y}
                              z={pointMeasurement.z}
                              baseLengthUnit={
                                props.measurementsProvider.lengthUnit
                              }
                              formattingSettings={props.measurementsProvider.formattingSettings()}
                              onDelete={() => {
                                handleDelete(measurement.id);
                              }}
                              onEdit={() => {
                                setEditingMeasurement(pointMeasurement);
                                handleEditPointMeasurement();
                              }}
                              onClick={() => {
                                props.onMeasurementClick?.(measurement.id);
                              }}
                              isSelected={props.measurementsProvider.isItemSelected(
                                measurement.id
                              )}
                              color={pointMeasurement.markups[0].color}
                            />
                          );
                        })()}
                      </Match>
                      <Match when={isAngleMeasurement(measurement)}>
                        <MeasurementAngleItem
                          value={(measurement as AngleMeasurementType).value}
                          baseAngleUnit={props.measurementsProvider.angleUnit}
                          formattingSettings={props.measurementsProvider.formattingSettings()}
                          onDelete={() => {
                            handleDelete(measurement.id);
                          }}
                          onClick={() => {
                            props.onMeasurementClick?.(measurement.id);
                          }}
                          isSelected={props.measurementsProvider.isItemSelected(
                            measurement.id
                          )}
                          color={measurement.markups[0].color}
                        />
                      </Match>
                      <Match when={isPolylineMeasurement(measurement)}>
                        <MeasurementPolylineItem
                          value={(measurement as PolylineMeasurementType).value}
                          baseLengthUnit={props.measurementsProvider.lengthUnit}
                          formattingSettings={props.measurementsProvider.formattingSettings()}
                          onDelete={() => {
                            handleDelete(measurement.id);
                          }}
                          onClick={() => {
                            props.onMeasurementClick?.(measurement.id);
                          }}
                          isSelected={props.measurementsProvider.isItemSelected(
                            measurement.id
                          )}
                          color={measurement.markups[0].color}
                        />
                      </Match>
                    </Switch>
                  )}
                </For>
              </div>
            </Show>
            <Show when={props.showSaveButton}>
              <div class="flex justify-center mb-2">
                <Button
                  color={BtnClasses.PRIMARY}
                  size="medium"
                  type="solid"
                  content="Save"
                  onClick={() => {
                    props.onSaveMeasurements?.();
                  }}
                />
              </div>
            </Show>
            <EditPointMeasurement
              editPointModalRef={setEditPointModalRef}
              pointColor={editingColor()}
              pointLabel={
                editingMeasurement() ? editingMeasurement()?.label : ''
              }
              getRGBColorString={(color: RgbColor) => getRGBColorString(color)}
              onClose={handleEditPointModalClose}
              onSave={handleEditPointModalSave}
            />
          </div>
        }
      />
    </div>
  );
};
